import { SearchOutlined } from '@ant-design/icons';
import { Checkbox, InputNumber, Slider } from 'antd';
import { extent } from 'd3-array';
import { get, isArray, isEmpty, isNil, isNumber, uniqBy } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { getSeverityAndCriticalFilter } from 'components/DataTable/customFilters';
import { Button, DatePicker } from 'components/ui';
import { AutoFocus } from 'horizon/components/AutoFocus';
import { DATE_FORMAT, isTableFilterOperator } from 'utils/constants';
import { formatDate } from 'utils/format';

import { FilterDropdown } from './FilterForm';
import {
  BoldText,
  ConditionalFiltersDivider,
  ConditionalFiltersLabel,
  Container,
  CustomFilterContainer,
  CustomFilterPopover,
  EmptyConditionalFiltersText,
  FilterSearchInput,
  InputNumberContainer,
  InputNumberLabel,
  ListItem,
  NoneText,
  Text,
} from './filters.style';
import { useFilterListMaxHeight } from './useFilterListMaxHeight';

const NONE_VALUE = ' ';

const getFilterOptionText = filter => {
  const { text, value } = filter;

  if (typeof text === 'string') {
    return text;
  }

  if (text?.props?.children) {
    return String(text.props.children);
  }

  return String(value ?? '');
};

// Builds filter options for table filter dropdown
export const getFilter = ({ values } = {}) => {
  return uniqBy(values, 'id')
    .filter(Boolean)
    .map(value => ({
      text: value.label,
      value: value.id || ' ',
    }));
};

// builds onFilter functions for client-side table filtering
const onFilterString = (key, exactMatch) => (filter, record) => {
  const search = filter.toString().toLowerCase();
  const string = key instanceof Array ? key.map(k => get(record, k)) : get(record, key);
  const value = String(string).toLowerCase();

  return exactMatch ? search === value : search.includes(value) || value.includes(search);
};

const onFilterArray = (key, filterAttr) => (filter, record) => {
  const search = filter.toString().toLowerCase();
  const value = get(record, key);
  return value
    .map(v => v[filterAttr])
    .join(' ')
    .toLowerCase()
    .includes(search);
};

const onFilterRange = (key, type, includeNoneOption) => (filter, record) => {
  /**
   * This is a short circuit for filters come from column definitions that filter
   * multiple properties. Such filters are applied in the ClientDataTable during
   * rendering. See "Columns that filter multiple properties" in
   * src/components/DataTable/README.md for more info.
   */
  if (filter.hasOwnProperty('key')) {
    return true;
  }

  const value = get(record, key);
  // Account for a none option before we try to check for min/max values
  if (includeNoneOption && filter === NONE_VALUE) {
    return value === '' || value === null || value === undefined;
  }

  let [min, max = Infinity] = filter || [];

  // Filter for arrays returns the array as a concatenated string when prioritizing a table.
  // To combat this we check if the filter is a string and split it appropriately.
  if (typeof filter === 'string') {
    const filterStringToArray = filter.split(',');
    [min, max] = filterStringToArray;
  }

  // if value is a date, format it for comparison
  if (type === 'date') {
    const exclusiveMax = formatDate(moment(max).add(1, 'day'));
    return value >= min && value < exclusiveMax;
  }

  return value >= min && value <= max;
};

export const getOnFilter = ({ key, type, filterAttr, exactMatch, values, includeNoneOption }) => {
  const exact = exactMatch !== null ? exactMatch : Boolean(values) && type === 'string';
  if (type === 'number' || type === 'date') {
    return onFilterRange(key, type, includeNoneOption);
  }
  if (type === 'array') {
    return onFilterArray(key, filterAttr);
  }
  return onFilterString(key, exact);
};

export const OkResetButtons = ({
  confirm,
  clearFilters,
  disabled,
  selectAll,
  handleSelectAll,
  handleSelectInverse,
  hasNoneOption,
  handleToggleNoneOption,
  noneOptionChecked,
  noneOptionLabel,
  allChecked,
  indeterminate,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const border = `1px solid ${theme.borderAccentColor}`;

  return (
    <div
      style={{
        borderTop: selectAll || handleSelectInverse || hasNoneOption ? border : 'none',
      }}
    >
      {selectAll && (
        <ListItem onClick={handleSelectAll} $checked={allChecked}>
          <Checkbox
            aria-label={t('inspection.selectAll', 'Select All')}
            onChange={handleSelectAll}
            checked={allChecked}
            indeterminate={indeterminate}
          />
          <BoldText>{t('inspection.selectAll', 'Select All')}</BoldText>
        </ListItem>
      )}
      {hasNoneOption && (
        <ListItem onClick={handleToggleNoneOption} $checked={noneOptionChecked}>
          <Checkbox
            aria-label={noneOptionLabel ?? t('common.none', 'none')}
            onChange={handleToggleNoneOption}
            checked={noneOptionChecked}
          />
          {noneOptionLabel ? <Text>{noneOptionLabel}</Text> : <NoneText />}
        </ListItem>
      )}
      {handleSelectInverse && (
        <ListItem>
          <Button
            onClick={handleSelectInverse}
            size="small"
            type="link"
            title={t(
              'components.selectInverseTooltip',
              'Remove selected filters and select unselected filters'
            )}
          >
            {t('components.selectInverse', 'Select Inverse')}
          </Button>
        </ListItem>
      )}
      <div
        className="ant-table-filter-dropdown-btns"
        style={{ borderTop: selectAll ? 'none' : border }}
      >
        <Button type="link" size="small" onClick={clearFilters}>
          {t('table.reset', 'Reset')}
        </Button>
        <Button type="primary" size="small" onClick={confirm} disabled={disabled}>
          {t('table.ok', 'OK')}
        </Button>
      </div>
    </div>
  );
};

export const getNumberPickerProps = (values, step, selectedKeys, isNull) => {
  const rawValues = values.map(v => v.label);
  const [rawMin, rawMax] = extent(rawValues);
  const min = rawMin === -Infinity ? undefined : Math.floor(rawMin);
  const max = rawMax === Infinity ? undefined : Math.ceil(rawMax);

  const [[selectedMin = min, selectedMax = max] = []] = selectedKeys || [];

  return {
    min,
    max,
    value: isNull ? [min, max] : [selectedMin, selectedMax],
    step: step || 0.1, // step is sometimes null so can't use default val
  };
};

export const getNumberPicker =
  (values, roundValue, step, includeSlider, includeNoneOption, noneOptionValue) =>
  ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const noneValue = noneOptionValue ?? NONE_VALUE;
    const isNull = selectedKeys.some(value => value === noneValue);

    const { min, max, value, ...rest } = getNumberPickerProps(values, step, selectedKeys, isNull);

    const handleInputChange = ({ updated: _updated, isMin }) => {
      if (isNaN(_updated)) {
        return;
      }

      const updated =
        roundValue && isNumber(step) && isNumber(_updated)
          ? Math.round(_updated / step) * step
          : _updated;

      if (isMin) {
        setSelectedKeys([[updated, value[1]]]);
      } else {
        setSelectedKeys([[value[0], updated]]);
      }
    };

    const handleConfirm = () => {
      if (isEmpty(selectedKeys) && value !== undefined) {
        setSelectedKeys([[value[0], value[1]]]);
      }
      confirm({ closeDropdown: true });
    };

    // clearFilters only resets the draft; confirm applies it.
    const handleClearFilters = () => {
      clearFilters();
      confirm({ closeDropdown: true });
    };

    const hasValidValues =
      (isNumber(value[0]) || value[0] === noneValue || (isNil(min) && isNil(value[0]))) &&
      (isNumber(value[1]) || value[1] === noneValue || (isNil(max) && isNil(value[1])));

    return (
      <AutoFocus elementToFocus={inputRef}>
        <CustomFilterPopover className="ant-table-filter-dropdown">
          <CustomFilterContainer>
            {includeSlider && (
              <Slider
                range
                // need to send all values as a nested array so the onFilters can handle ranges
                onChange={value => setSelectedKeys([value])}
                min={min}
                max={max}
                value={value}
                disabled={isNull}
                {...rest}
              />
            )}
            <InputNumberContainer>
              <div>
                <InputNumberLabel>{t('settings.minimum', 'Minimum')}</InputNumberLabel>
                <InputNumber
                  ref={inputRef}
                  placeholder={t('components.min', 'Min')}
                  min={min}
                  max={value[1] ? value[1] : max}
                  value={value[0]}
                  onChange={updated => handleInputChange({ updated, isMin: true })}
                  style={{ width: '75px' }}
                  disabled={isNull}
                  {...rest}
                />
              </div>
              <div>
                <InputNumberLabel>{t('settings.maximum', 'Maximum')}</InputNumberLabel>
                <InputNumber
                  placeholder={t('components.max', 'Max')}
                  min={value[0] ? value[0] : min}
                  max={max}
                  value={value[1]}
                  onChange={updated => handleInputChange({ updated, isMin: false })}
                  style={{ width: '75px' }}
                  disabled={isNull}
                  {...rest}
                />
              </div>
            </InputNumberContainer>
          </CustomFilterContainer>
          <OkResetButtons
            confirm={handleConfirm}
            clearFilters={handleClearFilters}
            disabled={!hasValidValues}
            hasNoneOption={includeNoneOption}
            noneOptionChecked={selectedKeys.includes(noneValue)}
            handleToggleNoneOption={event => {
              if (event.target.checked) {
                setSelectedKeys([noneValue]);
              } else {
                setSelectedKeys(selectedKeys.filter(k => k !== noneValue));
              }
            }}
          />
        </CustomFilterPopover>
      </AutoFocus>
    );
  };

const getDatePicker =
  (values, includeNoneOption, noneOptionValue, dateRangeLimit) =>
  ({ setSelectedKeys, selectedKeys = [], confirm, clearFilters }) => {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const [selectedRange] = selectedKeys || [];
    const noneValue = noneOptionValue ?? NONE_VALUE;
    const isNull = selectedKeys.some(value => value === noneValue);
    const [dates, setDates] = useState(null);

    // rc-picker resets a half-picked range whenever `value` changes identity, and picking a start
    // date re-renders through `setDates`.
    const defaultValue = useMemo(
      () =>
        isArray(selectedRange) && selectedRange.length ? selectedRange.map(d => moment(d)) : [],
      [selectedRange]
    );
    const currentYear = moment().year();
    const presets = [
      {
        label: t('components.last30Days', 'Last 30 Days'),
        value: [moment().subtract(30, 'days'), moment()],
      },
      {
        label: t('components.last3Months', 'Last 3 Months'),
        value: [moment().subtract(3, 'months'), moment()],
      },
      {
        label: t('components.last6Months', 'Last 6 Months'),
        value: [moment().subtract(6, 'months'), moment()],
      },
      {
        label: t('components.yearToDate', 'Year to Date'),
        value: [moment().startOf('year'), moment()],
      },
      { label: `${currentYear - 1}`, value: getYearRange(currentYear - 1) },
      { label: `${currentYear - 2}`, value: getYearRange(currentYear - 2) },
    ];

    const disabledDate = current => {
      if (!dates || !dateRangeLimit) {
        return false;
      }
      const tooLate = dates[0] && current.diff(dates[0], 'days') > dateRangeLimit;
      const tooEarly = dates[1] && dates[1].diff(current, 'days') > dateRangeLimit;
      return !!tooEarly || !!tooLate;
    };

    const handleConfirm = () => {
      confirm({ closeDropdown: true });
    };

    // clearFilters only resets the draft; confirm applies it.
    const handleClearFilters = () => {
      clearFilters();
      setSelectedKeys([]);
      confirm({ closeDropdown: true });
    };

    return (
      <AutoFocus elementToFocus={inputRef}>
        <CustomFilterPopover className="ant-table-filter-dropdown">
          <CustomFilterContainer>
            <DatePicker.RangePicker
              ref={inputRef}
              onChange={dates => {
                if (dates && dates[0] && dates[1] && dates[0].isSameOrBefore(dates[1])) {
                  // need to send all values as a nested array so the onFilters can handle ranges
                  setSelectedKeys([dates.map(formatDate)]);
                } else {
                  setSelectedKeys([]);
                }
              }}
              onCalendarChange={dates => setDates(dates)}
              disabledDate={disabledDate}
              placement="bottomRight"
              format={DATE_FORMAT}
              value={defaultValue}
              presets={presets}
              disabled={isNull}
            />
          </CustomFilterContainer>
          <OkResetButtons
            confirm={handleConfirm}
            clearFilters={handleClearFilters}
            hasNoneOption={includeNoneOption}
            noneOptionChecked={selectedKeys.includes(noneValue)}
            handleToggleNoneOption={event => {
              if (event.target.checked) {
                setSelectedKeys([noneValue]);
              } else {
                setSelectedKeys(selectedKeys.filter(k => k !== noneValue));
              }
            }}
          />
        </CustomFilterPopover>
      </AutoFocus>
    );
  };

const getDefaultFilterDropdown = initialProps => props => (
  <FilterDropdown {...initialProps} {...props} />
);

const getYearRange = year => [
  moment().startOf('year').year(year),
  moment().endOf('year').year(year),
];

const getSelectAll =
  ({
    includeNoneOption,
    noneOptionValue,
    noneOptionLabel,
    conditionalValues,
    /** Optional ref so callers can update suggestions without remounting this dropdown. */
    conditionalValuesRef,
    conditionalEmptyText,
    conditionalLabel,
    remainingLabel,
    selectedLabel,
    includeSearch = false,
    ..._rest
  }) =>
  ({ confirm, clearFilters, selectedKeys, setSelectedKeys, filters = [], visible }) => {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const popoverMaxHeight = useFilterListMaxHeight({ visible });
    const searchInputRef = useRef(null);
    const [searchText, setSearchText] = useState('');
    const { value: noneValuefilter } = filters.find(f => f.text === 'none') ?? { value: '' };
    const noneValue = noneOptionValue ?? (includeNoneOption ? NONE_VALUE : noneValuefilter);
    // None is exclusive if the operator is IS_NULL, but an empty string value can be included with other filter values
    const noneIsExclusive = isTableFilterOperator(noneValue);
    const visibleFilters = filters.filter(f => f.text !== 'none' && f.value !== noneValue);
    const resolvedConditionalValues = conditionalValuesRef?.current ?? conditionalValues;
    const hasConditionalGrouping = Array.isArray(resolvedConditionalValues);
    const conditionalValueSet = new Set(
      (resolvedConditionalValues ?? []).map(value => (value === '' ? noneValue : String(value)))
    );
    const selectedValueSet = new Set((selectedKeys ?? []).map(String));
    const isSelected = ({ value }) => selectedValueSet.has(String(value));
    const selectedFilters = visibleFilters.filter(isSelected);
    const conditionalFilters = visibleFilters.filter(
      filter => !isSelected(filter) && conditionalValueSet.has(String(filter.value))
    );
    const remainingFilters = visibleFilters.filter(
      filter => !isSelected(filter) && !conditionalValueSet.has(String(filter.value))
    );
    const trimmedSearch = searchText.trim().toLowerCase();

    // Clear search when the dropdown closes (OK / Reset / click-away), matching SiteFilter.
    useEffect(() => {
      if (!visible) {
        setSearchText('');
      }
    }, [visible]);
    const matchesSearch = filter => {
      if (!trimmedSearch) {
        return true;
      }

      return getFilterOptionText(filter).toLowerCase().includes(trimmedSearch);
    };
    const filteredSelectedFilters = selectedFilters.filter(matchesSearch);
    const filteredConditionalFilters = conditionalFilters.filter(matchesSearch);
    const filteredRemainingFilters = remainingFilters.filter(matchesSearch);
    const searchFilteredVisibleFilters = trimmedSearch
      ? visibleFilters.filter(matchesSearch)
      : visibleFilters;
    const firstVisibleFilter = (
      hasConditionalGrouping
        ? [...filteredSelectedFilters, ...filteredConditionalFilters, ...filteredRemainingFilters]
        : searchFilteredVisibleFilters
    )[0];
    const visibleFilterValues = searchFilteredVisibleFilters.map(({ value }) => value);
    const selectedVisibleFilterCount = visibleFilterValues.filter(value =>
      selectedKeys?.includes(value)
    ).length;

    const noneOptionChecked = (selectedKeys ?? []).includes(noneValue);
    // A combinable none option is part of "all", so the tri-state box tracks it too, and it
    // stays selectable when search hides every ordinary option.
    const noneCountsTowardAll = includeNoneOption && !noneIsExclusive;
    const hasSelectableOptions = visibleFilterValues.length > 0 || noneCountsTowardAll;
    const allChecked =
      hasSelectableOptions &&
      selectedVisibleFilterCount === visibleFilterValues.length &&
      (!noneCountsTowardAll || noneOptionChecked);
    const indeterminate =
      (selectedVisibleFilterCount > 0 || (noneCountsTowardAll && noneOptionChecked)) && !allChecked;
    /**
     * Preserve selections hidden by the active search; bulk actions only touch the visible subset.
     * The none option is pinned in the footer and never hidden by search, so bulk actions own it
     * when it is combinable and drop it when it is an exclusive operator sentinel.
     */
    const outOfScopeKeys = (selectedKeys ?? []).filter(
      key => !visibleFilterValues.includes(key) && key !== noneValue
    );
    const hasNoSearchMatches =
      Boolean(trimmedSearch) &&
      (hasConditionalGrouping
        ? !filteredSelectedFilters.length &&
          !filteredConditionalFilters.length &&
          !filteredRemainingFilters.length
        : !searchFilteredVisibleFilters.length);
    const noMatchesText = t('table.noMatches', 'No matches');
    // Hide bare group headers when search leaves one side empty.
    const showConditionalGroup =
      filteredConditionalFilters.length > 0 ||
      (Boolean(conditionalEmptyText) && !trimmedSearch && selectedFilters.length === 0);
    const showRemainingGroup = filteredRemainingFilters.length > 0;

    const bulkNoneKeys = noneCountsTowardAll ? [noneValue] : [];

    const handleSelectAll = () => {
      setSelectedKeys(
        allChecked ? outOfScopeKeys : [...outOfScopeKeys, ...visibleFilterValues, ...bulkNoneKeys]
      );
    };

    const handleSelectInverse = () => {
      setSelectedKeys([
        ...outOfScopeKeys,
        ...visibleFilterValues.filter(value => !selectedKeys.includes(value)),
        ...(noneOptionChecked ? [] : bulkNoneKeys),
      ]);
    };

    const handleChange = value => {
      const index = selectedKeys.findIndex(k => k === value);

      // An exclusive none sentinel replaces the whole selection rather than joining it.
      if (value === noneValue && noneIsExclusive) {
        return setSelectedKeys(index === -1 ? [value] : []);
      }

      if (index === -1) {
        const filteredSelections = noneIsExclusive
          ? selectedKeys.filter(key => key !== noneValue)
          : selectedKeys;
        setSelectedKeys([...filteredSelections, value]);
      } else {
        const selectedClone = [...selectedKeys];
        selectedClone.splice(index, 1);
        setSelectedKeys(selectedClone);
      }
    };

    const handleConfirm = () => {
      setSearchText('');
      confirm({ closeDropdown: true });
    };

    // clearFilters only resets the draft; confirm applies it.
    const handleClearFilters = () => {
      setSearchText('');
      clearFilters?.();
      confirm({ closeDropdown: true });
    };

    const renderFilter = f => (
      <ListItem
        className="ant-dropdown-menu-item"
        key={f.value}
        onClick={() => handleChange(f.value)}
        $checked={selectedKeys?.some(key => key === f.value)}
      >
        <Checkbox
          ref={f === firstVisibleFilter && !includeSearch ? inputRef : undefined}
          aria-label={getFilterOptionText(f)}
          onChange={() => handleChange(f.value)}
          checked={selectedKeys?.some(key => key === f.value)}
        />
        <Text>{f.text}</Text>
      </ListItem>
    );

    return (
      <AutoFocus elementToFocus={includeSearch ? searchInputRef : inputRef}>
        <CustomFilterPopover
          className="ant-table-filter-dropdown ant-table-filter-dropdown-checkboxes"
          $maxHeight={popoverMaxHeight}
        >
          {includeSearch && (
            <CustomFilterContainer>
              <FilterSearchInput
                ref={searchInputRef}
                allowClear
                onChange={event => setSearchText(event.target.value)}
                onPressEnter={handleConfirm}
                placeholder={t('common.search', 'Search')}
                suffix={searchText ? undefined : <SearchOutlined aria-hidden />}
                value={searchText}
              />
            </CustomFilterContainer>
          )}
          <Container role="group" aria-label={t('table.filterOptions', 'Filter options')}>
            {hasNoSearchMatches ? (
              <EmptyConditionalFiltersText>{noMatchesText}</EmptyConditionalFiltersText>
            ) : hasConditionalGrouping ? (
              <>
                {filteredSelectedFilters.length > 0 && (
                  <>
                    {selectedLabel && (
                      <ConditionalFiltersLabel>{selectedLabel}</ConditionalFiltersLabel>
                    )}
                    {filteredSelectedFilters.map(renderFilter)}
                    {(showConditionalGroup || showRemainingGroup) && <ConditionalFiltersDivider />}
                  </>
                )}
                {showConditionalGroup && (
                  <>
                    {conditionalLabel && (
                      <ConditionalFiltersLabel>{conditionalLabel}</ConditionalFiltersLabel>
                    )}
                    {filteredConditionalFilters.length
                      ? filteredConditionalFilters.map(renderFilter)
                      : conditionalEmptyText && (
                          <EmptyConditionalFiltersText>
                            {conditionalEmptyText}
                          </EmptyConditionalFiltersText>
                        )}
                  </>
                )}
                {showConditionalGroup && showRemainingGroup && <ConditionalFiltersDivider />}
                {showRemainingGroup && (
                  <>
                    {remainingLabel && (
                      <ConditionalFiltersLabel>{remainingLabel}</ConditionalFiltersLabel>
                    )}
                    {filteredRemainingFilters.map(renderFilter)}
                  </>
                )}
              </>
            ) : (
              searchFilteredVisibleFilters.map(renderFilter)
            )}
          </Container>
          <OkResetButtons
            confirm={handleConfirm}
            clearFilters={handleClearFilters}
            selectAll={true}
            indeterminate={indeterminate}
            allChecked={allChecked}
            handleSelectAll={handleSelectAll}
            handleSelectInverse={handleSelectInverse}
            hasNoneOption={includeNoneOption}
            noneOptionChecked={noneOptionChecked}
            noneOptionLabel={noneOptionLabel}
            handleToggleNoneOption={() => handleChange(noneValue)}
          />
        </CustomFilterPopover>
      </AutoFocus>
    );
  };

export const getFilterDropdown = (initialProps = {}) => {
  const {
    values = [],
    type = 'default',
    step,
    roundValue = false,
    includeSlider = true,
    includeNoneOption = false,
    noneOptionValue = ' ',
    definition,
    criticalOnly = false,
    dateRangeLimit,
  } = initialProps;

  if (definition?.name === 'Severity') {
    return getSeverityAndCriticalFilter(
      values,
      roundValue,
      step,
      includeSlider,
      includeNoneOption,
      noneOptionValue,
      criticalOnly
    );
  }
  if (type === 'number') {
    return getNumberPicker(
      values,
      roundValue,
      step,
      includeSlider,
      includeNoneOption,
      noneOptionValue
    );
  }
  if (type === 'date') {
    return getDatePicker(values, includeNoneOption, noneOptionValue, dateRangeLimit);
  }
  if (type === 'string') {
    return getSelectAll(initialProps);
  }
  // FIXME ARS - hate this, but until I rip out filterProperty this is the easiest way to keep existing functionality
  if (type === 'default') {
    return getDefaultFilterDropdown(initialProps);
  }
};

// Limit filter options/ranges when filtering damage subsets client-side
export const limitFiltersFromData = (col, data = []) => {
  if (!data || !data.length || !col.type) {
    return col;
  }

  const config = {
    type: col.type,
    values: data.map(d => ({
      id: get(d, col.filterProperty, ''),
      label: get(d, col.filterProperty, ''),
    })),
  };

  return {
    ...col,
    filters: getFilter(config),
    filterDropdown: getFilterDropdown(config),
  };
};
